// Storage helpers for invoices / photos / reports / attachments.
// Convention: user-scoped files live under `${userId}/...` so bucket policies
// (auth.uid()::text = first folder) grant access to the owner.
import { supabase } from "@/integrations/supabase/client";
import type { StorageBucket, ReviewFile } from "@/lib/db/models";

export interface UploadInput {
  bucket: StorageBucket;
  userId: string;
  reviewId?: string;
  file: File;
  kind?: "invoice" | "photo" | "report" | "supporting_document" | "attachment";
  /**
   * When true, skip duplicate detection even if the same customer already
   * uploaded a file with the same content. Staff/admin bulk imports can
   * opt in via this flag.
   */
  allowDuplicate?: boolean;
}

export interface DuplicateUploadInfo {
  fileHash: string;
  existingReviewId: string;
  existingFileName: string;
  existingUploadedAt: string;
}

/**
 * Thrown by fileService.upload when the same customer previously uploaded
 * an identical file (same sha256 hash). The submit flow can catch this and
 * show a friendly warning instead of proceeding.
 */
export class DuplicateUploadError extends Error {
  readonly duplicate: DuplicateUploadInfo;
  constructor(duplicate: DuplicateUploadInfo) {
    super("duplicate_upload");
    this.name = "DuplicateUploadError";
    this.duplicate = duplicate;
  }
}

/** Per-file upload cap. Matches Supabase Storage defaults and OCR practicality. */
export const MAX_UPLOAD_BYTES = 20 * 1024 * 1024; // 20 MB

/** Allowed MIME types for customer-uploaded invoices/photos/attachments. */
export const ALLOWED_UPLOAD_MIME = new Set<string>([
  "application/pdf",
  "image/jpeg",
  "image/jpg",
  "image/png",
  "image/webp",
  "image/heic",
  "image/heif",
  "image/gif",
  "image/tiff",
]);

export class InvalidUploadError extends Error {
  readonly code: "too_large" | "unsupported_type" | "empty";
  constructor(code: "too_large" | "unsupported_type" | "empty", message: string) {
    super(message);
    this.name = "InvalidUploadError";
    this.code = code;
  }
}

/** Client-side validation mirroring server-side storage constraints. */
export function validateUploadFile(file: File): void {
  if (!file.size) {
    throw new InvalidUploadError("empty", `"${file.name}" is empty.`);
  }
  if (file.size > MAX_UPLOAD_BYTES) {
    throw new InvalidUploadError(
      "too_large",
      `"${file.name}" is ${(file.size / 1024 / 1024).toFixed(1)} MB. Maximum is 20 MB.`,
    );
  }
  // Some browsers report an empty type for HEIC etc. Allow empty type only
  // when the extension is on the safe list.
  const type = (file.type || "").toLowerCase();
  if (type && !ALLOWED_UPLOAD_MIME.has(type)) {
    throw new InvalidUploadError(
      "unsupported_type",
      `"${file.name}" is not a supported file type. Upload a PDF or image.`,
    );
  }
  if (!type) {
    const ext = file.name.toLowerCase().split(".").pop() ?? "";
    const okExt = ["pdf", "jpg", "jpeg", "png", "webp", "heic", "heif", "gif", "tif", "tiff"];
    if (!okExt.includes(ext)) {
      throw new InvalidUploadError(
        "unsupported_type",
        `"${file.name}" is not a supported file type. Upload a PDF or image.`,
      );
    }
  }
}

/** SHA-256 of the file's bytes, hex-encoded. Runs in the browser via Web Crypto. */
export async function computeFileHash(file: File): Promise<string> {
  const buf = await file.arrayBuffer();
  const digest = await crypto.subtle.digest("SHA-256", buf);
  const bytes = new Uint8Array(digest);
  let out = "";
  for (const b of bytes) out += b.toString(16).padStart(2, "0");
  return out;
}

export const fileService = {
  computeFileHash,

  buildPath(userId: string, reviewId: string | undefined, fileName: string) {
    const scope = reviewId ? `${userId}/${reviewId}` : userId;
    const safe = fileName.replace(/[^a-zA-Z0-9._-]/g, "_");
    return `${scope}/${Date.now()}-${safe}`;
  },

  /**
   * Look up an existing file with the same sha256 previously uploaded by the
   * same customer. Returns null when none is found or the lookup errors
   * (dedupe is best-effort — never blocks uploads on infra failures).
   */
  async findDuplicateForUser(
    userId: string,
    fileHash: string,
  ): Promise<DuplicateUploadInfo | null> {
    if (!fileHash) return null;
    const { data, error } = await supabase
      .from("review_files")
      .select("review_id,file_name,created_at,file_hash")
      .eq("uploaded_by", userId)
      .eq("file_hash", fileHash)
      .order("created_at", { ascending: false })
      .limit(1)
      .maybeSingle();
    if (error || !data) return null;
    return {
      fileHash,
      existingReviewId: data.review_id,
      existingFileName: data.file_name,
      existingUploadedAt: data.created_at,
    };
  },

  async upload({ bucket, userId, reviewId, file, kind, allowDuplicate }: UploadInput) {
    // Enforce size + type before spending bandwidth on hash or upload.
    validateUploadFile(file);
    // Hash first so we can short-circuit before touching storage.
    let fileHash: string | null = null;
    try {
      fileHash = await computeFileHash(file);
    } catch {
      // Web Crypto unavailable (e.g. non-secure context). Skip dedupe rather
      // than blocking the upload.
      fileHash = null;
    }

    if (fileHash && !allowDuplicate) {
      const dupe = await this.findDuplicateForUser(userId, fileHash);
      if (dupe) throw new DuplicateUploadError(dupe);
    }

    const path = this.buildPath(userId, reviewId, file.name);
    const { error: upErr } = await supabase.storage
      .from(bucket)
      .upload(path, file, { cacheControl: "3600", upsert: false });
    if (upErr) throw upErr;

    // Optionally record the file if it's tied to a review
    if (reviewId) {
      const { data, error } = await supabase
        .from("review_files")
        .insert({
          review_id: reviewId,
          bucket,
          storage_path: path,
          file_name: file.name,
          file_size: file.size,
          mime_type: file.type,
          kind: kind ?? null,
          uploaded_by: userId,
          file_hash: fileHash,
        })
        .select()
        .single();
      if (error) throw error;
      return data as ReviewFile;
    }
    return null;
  },

  async listForReview(reviewId: string): Promise<ReviewFile[]> {
    const { data, error } = await supabase
      .from("review_files")
      .select("*")
      .eq("review_id", reviewId)
      .order("created_at", { ascending: false });
    if (error) throw error;
    return (data ?? []) as ReviewFile[];
  },

  async signedUrl(bucket: StorageBucket, path: string, expiresIn = 3600) {
    const { data, error } = await supabase.storage.from(bucket).createSignedUrl(path, expiresIn);
    if (error) throw error;
    return data.signedUrl;
  },

  async remove(bucket: StorageBucket, path: string) {
    const { error } = await supabase.storage.from(bucket).remove([path]);
    if (error) throw error;
  },
};
